/**
 * MoneyUtils — currency handling for smallest-unit storage.
 * Money is stored in the smallest currency unit (e.g. paisa for INR, cents for USD).
 * Ported from owners_dashboard/src/utils/moneyUtils.js
 *
 * Usage:
 *   MoneyUtils.setCurrency(restaurant.currencyConfig);  // call once on restaurant load
 *   MoneyUtils.toSmallestUnit(99.99)   // → 9999
 *   MoneyUtils.toDisplayAmount(9999)   // → 99.99
 *   MoneyUtils.format(9999)            // → "₹99.99"
 *   MoneyUtils.formatDouble(99.99)     // → "₹99.99"
 */

export class MoneyUtils {
  static _config = {
    code: 'INR',
    symbol: '₹',
    decimalPlaces: 2,
    symbolPosition: 'before',
    multiplier: 100,
  };

  /**
   * Set the currency configuration from restaurant settings.
   * Call this once when the restaurant data loads.
   * @param {Object} config - currencyConfig from the restaurant model
   */
  static setCurrency(config) {
    if (!config) return;
    this._config = {
      code: config.code || 'INR',
      symbol: config.symbol || '₹',
      decimalPlaces: config.decimalPlaces !== undefined ? config.decimalPlaces : 2,
      symbolPosition: config.symbolPosition || 'before',
      multiplier: Math.pow(10, config.decimalPlaces !== undefined ? config.decimalPlaces : 2),
    };
  }

  static get config() {
    return this._config;
  }

  /**
   * Convert display amount (e.g. 99.99) → smallest unit (e.g. 9999 paisa)
   */
  static toSmallestUnit(amount) {
    if (amount === undefined || amount === null) return 0;
    return Math.round(Number(amount) * this._config.multiplier);
  }

  /**
   * Convert smallest unit (e.g. 9999 paisa) → display amount (e.g. 99.99)
   */
  static toDisplayAmount(smallestUnit) {
    if (smallestUnit === undefined || smallestUnit === null) return 0;
    return Number(smallestUnit) / this._config.multiplier;
  }

  /**
   * Format smallest unit as currency string → "₹99.99"
   */
  static format(smallestUnit) {
    const value = this.toDisplayAmount(smallestUnit);
    const formatted = value.toFixed(this._config.decimalPlaces);
    return this._config.symbolPosition === 'before'
      ? `${this._config.symbol}${formatted}`
      : `${formatted}${this._config.symbol}`;
  }

  /**
   * Format a plain float/double as currency string → "₹99.99"
   * Use this when you already have the display value (not smallest unit).
   */
  static formatDouble(amount) {
    if (amount === undefined || amount === null) return '';
    const formatted = Number(amount).toFixed(this._config.decimalPlaces);
    return this._config.symbolPosition === 'before'
      ? `${this._config.symbol}${formatted}`
      : `${formatted}${this._config.symbol}`;
  }
}

export default MoneyUtils;
